/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import {
  AdPlaybackPolicy,
  PlaybackAutoSeekMode,
  PlaybackFastForwardMode,
  PlaybackInterruptedMode,
  PlaybackRepeatedMode,
  PlaybackRewindMode,
  Player,
} from '@quickplay/player-interface';
import { Logger, PlatformUtil } from '@quickplay/foundation';
import { Ad, Avail } from '../../model/AdMetadata';
import {
  getAdBreaksBetween,
  getAdvertBreaksDurationPriorTo,
  getTotalAdvertsBreaksDuration,
  isAdBreaksAvailable,
} from './AdPolicyUtil';

enum ActivePolicy {
  IDLE = 'idle',
  AUTO_SEEK = 'autoseek',
}

export class MediatailorPlaybackPolicyHandler {
  /**
   * The array of all ad breaks.
   * @private
   */
  private adBreaks: Avail[] | null = null;
  private readonly fastForwardMode: PlaybackFastForwardMode = PlaybackFastForwardMode.TRICK_FW_PLAY_ALL;
  private readonly rewindMode: PlaybackRewindMode = PlaybackRewindMode.TRICK_RW_PLAY_ALL;
  private readonly autoSeekMode: PlaybackAutoSeekMode = PlaybackAutoSeekMode.AUTO_SEEK_PLAY_PREROLL;
  private readonly repeatMode: PlaybackRepeatedMode = PlaybackRepeatedMode.REPEAT_PLAY_ONCE;
  private readonly interruptMode: PlaybackInterruptedMode = PlaybackInterruptedMode.INTERRUPT_RESUME;
  private watchedAdBreaks: Avail[] = [];

  /**
   * An array that contains a list of seek positions to look for.
   * @private
   */
  private autoSeekPositions: number[] = [];
  /**
   * A value indicating the type of policy being applied.
   * @private
   */
  private policyInPlace: ActivePolicy;

  /**
   * The default tolerance in seconds for seek operations.
   * Note: When you seek to an exact segment boundary (e.g., 120.000), the PS5's MSE (Media Source Extensions) implementation faces ambiguity:
   * It may not correctly interpret the seek position, leading to potential playback issues.
   * To mitigate this, a small tolerance is added to the seek position.
   * Refer: [CBT-11251] for more details.
   *
   */
  private readonly DEFAULT_SEEK_TOLERANCE_SECS: number = 0.002;

  constructor(
    private readonly player: Player,
    adPlaybackPolicy: AdPlaybackPolicy,
    private readonly logger: Logger,
  ) {
    this.fastForwardMode = adPlaybackPolicy.fastForwardMode;
    this.rewindMode = adPlaybackPolicy.rewindMode;
    this.autoSeekMode = adPlaybackPolicy.autoSeekMode;
    this.repeatMode = adPlaybackPolicy.repeatMode;
    this.interruptMode = adPlaybackPolicy.interruptMode;

    this.logger.log(
      `[AD POLICY] [FastForwardMode: ${this.fastForwardMode}] [RewindMode: ${this.rewindMode}] [AutoSeekMode: ${
        this.autoSeekMode
      }] [RepeatMode: ${this.repeatMode}] [InterruptMode: ${this.interruptMode}] [InitialPlaybackTime: ${
        player.playbackOptions && player.playbackOptions.initialPlaybackTime
          ? player.playbackOptions.initialPlaybackTime
          : 0
      }]`,
    );

    this.policyInPlace = ActivePolicy.IDLE;
  }

  setAdBreaks(adBreaks: Avail[]): void {
    this.adBreaks = adBreaks;
  }

  playbackInterrupted(): void {
    // no-op
  }

  applySeekPlaybackPolicy = (position: number, adBreaks: Avail[]): number => {
    let startPosition: number | undefined = undefined;

    /**
     * Seek Playback policy;
     * - Forward playback policy: If the player'currentTime is less than
     * seekPosition.
     *  - Rewind playback policy: If the player'currentTime is greater than
     * seekPosition.
     */
    if (this.player.currentTime < position) {
      const midrollAdBreaks = this.getMidrollAdBreaks(this.player.currentTime, position, adBreaks);
      startPosition = this.playbackForwardPolicy(midrollAdBreaks);
      if (startPosition !== undefined) {
        this.autoSeekPositions.push(position);
        return startPosition;
      }
    } else if (this.player.currentTime > position) {
      const midrollAdBreaks = this.getMidrollAdBreaks(position, this.player.currentTime, adBreaks);
      startPosition = this.playbackRewindPolicy(midrollAdBreaks);
      if (startPosition !== undefined) {
        this.autoSeekPositions.push(position);
        return startPosition;
      }
    }

    return position;
  };
  private readonly getSeekTolerance = (): number => {
    return PlatformUtil.isPS5() ? this.DEFAULT_SEEK_TOLERANCE_SECS : 0;
  };

  private getMidrollAdBreaks(start: number, end: number, adBreaks: Avail[]): Avail[] {
    const filteredAdBreaks = getAdBreaksBetween(start, end, adBreaks);
    return filteredAdBreaks.filter(
      (adBreak: Avail) =>
        adBreak.startTimeInSeconds !== 0 &&
        (adBreak?.startTimeInSeconds ?? 0) + (adBreak?.durationInSeconds ?? 0) !== this.player.duration,
    );
  }

  playbackForwardPolicy(midrollAdBreaks: Avail[]): number | undefined {
    if (midrollAdBreaks.length) {
      switch (this.fastForwardMode) {
        case PlaybackFastForwardMode.TRICK_FW_PLAY_FIRST: {
          const firstAdBreak = midrollAdBreaks[0];

          if (firstAdBreak && this.playbackRepeatedRule(firstAdBreak)) {
            return firstAdBreak.startTimeInSeconds;
          }

          break;
        }
        case PlaybackFastForwardMode.TRICK_FW_PLAY_LAST: {
          const lastAdBreak = midrollAdBreaks[midrollAdBreaks.length - 1];

          if (lastAdBreak && this.playbackRepeatedRule(lastAdBreak)) {
            return lastAdBreak.startTimeInSeconds;
          }

          break;
        }
        case PlaybackFastForwardMode.TRICK_FW_SKIP_ALL: {
          break;
        }
        case PlaybackFastForwardMode.TRICK_FW_PLAY_ALL: {
          const position = this.handleFastForwardPlayAll(midrollAdBreaks);
          if (position !== undefined) {
            return position;
          }
          break;
        }
      }
    }

    return undefined;
  }

  private handleFastForwardPlayAll(midrollAdBreaks: Avail[]): number | undefined {
    // const firstAdBreak = midrollAdBreaks.shift();
    let firstAdBreak: Avail | undefined;

    midrollAdBreaks.forEach((adBreak: Avail) => {
      if (this.playbackRepeatedRule(adBreak)) {
        if (firstAdBreak === undefined) {
          firstAdBreak = adBreak;
        } else {
          adBreak.startTimeInSeconds && this.autoSeekPositions.push(adBreak.startTimeInSeconds);
        }
      }
    });
    return (firstAdBreak && firstAdBreak.startTimeInSeconds) ?? undefined;
  }
  setWatchedAdBreak(adBreak: Avail): void {
    // Check if the adbreak is already marked as watched to avoid adding up duplicate data in an array.
    if (this.repeatMode === PlaybackRepeatedMode.REPEAT_ALWAYS_PLAY || this.isAdBreakPresent(adBreak)) {
      return;
    }

    this.watchedAdBreaks.push(adBreak);
  }

  getWatchedAdBreaks(): Avail[] {
    return this.watchedAdBreaks;
  }

  private isAdBreakPresent(adBreak: Avail): boolean {
    return this.watchedAdBreaks.some(
      watchedAdBreak => watchedAdBreak.startTimeInSeconds === adBreak.startTimeInSeconds,
    );
  }

  playbackRewindPolicy(midrollAdBreaks: Avail[]): number | undefined {
    if (midrollAdBreaks.length) {
      switch (this.rewindMode) {
        case PlaybackRewindMode.TRICK_RW_PLAY_FIRST: {
          const firstAdBreak = midrollAdBreaks[length - 1];
          if (firstAdBreak && this.playbackRepeatedRule(firstAdBreak)) {
            return firstAdBreak.startTimeInSeconds;
          }

          break;
        }
        case PlaybackRewindMode.TRICK_RW_PLAY_LAST: {
          const lastAdBreak = midrollAdBreaks[0];
          if (lastAdBreak && this.playbackRepeatedRule(lastAdBreak)) {
            return lastAdBreak.startTimeInSeconds;
          }

          break;
        }
        case PlaybackRewindMode.TRICK_RW_SKIP_ALL: {
          break;
        }
        case PlaybackRewindMode.TRICK_RW_PLAY_ALL: {
          const position = this.handleFastForwardPlayAll(midrollAdBreaks);
          if (position !== undefined) {
            return position;
          }
          break;
        }
      }
    }
    return undefined;
  }

  playbackRepeatedRule = (adbreak: Avail): boolean => {
    if (this.watchedAdBreaks.length === 0) {
      return true;
    }
    switch (this.repeatMode) {
      case PlaybackRepeatedMode.REPEAT_ALWAYS_PLAY: {
        return true;
      }
      case PlaybackRepeatedMode.REPEAT_PLAY_ONCE: {
        if (this.isAdBreakPresent(adbreak)) {
          return false;
        }
      }
    }
    return true;
  };

  resolveSeekIntentForInitialPlayback = (position: number, adBreaks: Avail[]): number => {
    const filteredAdBreaks = getAdBreaksBetween(0, position, adBreaks);

    if (!filteredAdBreaks.length) {
      return position;
    }

    const midrollAdBreaks = filteredAdBreaks.filter(
      (adBreak: Avail) =>
        adBreak.startTimeInSeconds !== 0 &&
        (adBreak.startTimeInSeconds ?? 0) + (adBreak?.durationInSeconds ?? 0) !== this.player.duration,
    );

    switch (this.autoSeekMode) {
      case PlaybackAutoSeekMode.AUTO_SEEK_SKIP_ALL: {
        return position;
      }
      case PlaybackAutoSeekMode.AUTO_SEEK_PLAY_PREROLL: {
        const startPosition: number | undefined = this.playbackStartPositionForPreroll(filteredAdBreaks);

        if (startPosition !== undefined) {
          this.autoSeekPositions.push(position);
          return startPosition;
        }

        break;
      }
      case PlaybackAutoSeekMode.AUTO_SEEK_PLAY_MIDROLL: {
        const startPosition: number | undefined = this.playbackStartPositionForMidroll(
          this.fastForwardMode,
          midrollAdBreaks,
        );

        if (startPosition !== undefined) {
          this.autoSeekPositions.push(position);
          return startPosition;
        }

        break;
      }
      case PlaybackAutoSeekMode.AUTO_SEEK_PLAY_ALL: {
        const startPosition: number | undefined = this.playbackStartPositionForPlayAll(
          this.fastForwardMode,
          adBreaks,
          midrollAdBreaks,
        );

        if (startPosition !== undefined) {
          this.autoSeekPositions.push(position);
          return startPosition;
        }

        break;
      }
    }

    return position;
  };

  resolveSeekIntent = (position: number): number => {
    const nextSeekPosition: number | undefined = this.adBreakEnd();

    if (nextSeekPosition === undefined) {
      return position;
    }

    this.player.seek(nextSeekPosition + this.getSeekTolerance());

    return nextSeekPosition;
  };

  getAutoSeekPositionIfAny = (): number | null => {
    return null;
  };

  getContentPositionForPlayhead(playhead: number): number {
    return playhead - getAdvertBreaksDurationPriorTo(playhead, this.adBreaks ?? []);
  }

  getPlayheadForContentPosition(position: number): number {
    return position + getAdvertBreaksDurationPriorTo(position, this.adBreaks ?? []);
  }

  getContentDuration(): number {
    return this.player.duration - getTotalAdvertsBreaksDuration(this.adBreaks ?? []);
  }

  willSeekTo(position: number, isInitialPlayback: boolean = false): number {
    if (this.player.isLive || !this.adBreaks || !this.adBreaks.length) {
      return position;
    }

    if (isInitialPlayback || this.player.currentTime <= 0) {
      return this.resolveSeekIntentForInitialPlayback(position, this.adBreaks) + this.getSeekTolerance();
    }
    return this.applySeekPlaybackPolicy(position, this.adBreaks) + this.getSeekTolerance();
  }

  getCurrentTime(currentAdBreak: Avail | undefined): number {
    if (this.player.isLive || !isAdBreaksAvailable(this.adBreaks ?? [])) {
      return this.player.currentTime;
    }

    if (currentAdBreak) {
      // When in an ad break, return the content time position before the ad break started
      // This represents how much content was watched before this ad break
      const contentTimeBeforeAdBreak = Math.max(0, currentAdBreak.startTimeInSeconds ?? 0);
      const priorAdsDuration = getAdvertBreaksDurationPriorTo(
        currentAdBreak.startTimeInSeconds ?? 0,
        this.adBreaks ?? [],
      );
      return Math.max(0, contentTimeBeforeAdBreak - priorAdsDuration);
    } else {
      // When not in an ad break, calculate content time by subtracting all prior ad durations
      const playerTime = this.player.currentTime;
      const priorAdsDuration = getAdvertBreaksDurationPriorTo(playerTime, this.adBreaks ?? []);
      const contentTime = playerTime - priorAdsDuration;

      // If we get a negative result, it means we're likely in a pre-roll ad scenario
      // In this case, return 0 as content hasn't started yet
      return Math.max(0, contentTime);
    }
  }

  getDuration(currentAd: Ad | undefined): number {
    if (this.player.isLive || !isAdBreaksAvailable(this.adBreaks ?? []) || isNaN(this.player.duration)) {
      return this.player.duration;
    }

    if (currentAd) {
      const currentAdDuration_ = currentAd.durationInSeconds;

      return currentAdDuration_ ? this.player.duration : currentAdDuration_;
    } else if (!isNaN(this.player.duration)) {
      return this.player.duration - getTotalAdvertsBreaksDuration(this.adBreaks ?? []);
    }

    return this.player.duration;
  }

  discardAdBreak(currentAdBreak: Avail | undefined): void {
    if (!currentAdBreak) {
      return;
    }

    if (
      currentAdBreak.startTimeInSeconds === undefined ||
      currentAdBreak.durationInSeconds === undefined
    ) {
      return;
    }

    const skipToOffset = currentAdBreak.startTimeInSeconds + currentAdBreak.durationInSeconds;

    this.logger.log('[AD POLICY] Discarding ad break', {
      adBreakId: currentAdBreak.availId,
      from: this.player.currentTime,
      to: skipToOffset,
    });
    this.player.seek(skipToOffset);
  }

  skipAd(currentAd: Ad | undefined): void {
    if (!currentAd) {
      return;
    }

    const skipToOffset = (currentAd?.startTimeInSeconds ?? 0) + (currentAd?.durationInSeconds ?? 0);

    this.player.seek(skipToOffset);
  }

  private playbackStartPositionForPreroll = (adBreaks: Avail[]): number | undefined => {
    if (!adBreaks.length) {
      return undefined;
    }

    const firstAdBreak = adBreaks[0];

    if (firstAdBreak.startTimeInSeconds === 0) {
      return firstAdBreak.startTimeInSeconds;
    }

    return undefined;
  };

  private playbackStartPositionForMidroll = (
    _fastForwardMode: PlaybackFastForwardMode,
    midrollAdBreaks: Avail[],
  ): number | undefined => {
    if (midrollAdBreaks.length) {
      return this.playbackForwardPolicy(midrollAdBreaks);
    }

    return undefined;
  };

  private playbackStartPositionForPlayAll = (
    fastForwardMode: PlaybackFastForwardMode,
    adBreaks: Avail[],
    midrollAdBreaks: Avail[],
  ): number | undefined => {
    const prerollAdBreak = adBreaks[0];

    if (prerollAdBreak && prerollAdBreak.startTimeInSeconds === 0) {
      const autoSeekPosition = this.playbackForwardPolicy(midrollAdBreaks);
      if (autoSeekPosition) {
        this.autoSeekPositions.push(autoSeekPosition);
      }
      return prerollAdBreak.startTimeInSeconds;
    } else {
      const startPosition: number | undefined = this.playbackStartPositionForMidroll(fastForwardMode, midrollAdBreaks);

      if (startPosition !== undefined) {
        return startPosition;
      }
    }

    return undefined;
  };

  private adBreakEnd = (): number | undefined => {
    if (!this.autoSeekPositions.length) {
      return undefined;
    }

    const nextPlayhead: number | undefined = this.autoSeekPositions[0];

    if (nextPlayhead) {
      this.updateAutoSeekOnAdBreakEnd();
      return nextPlayhead;
    }

    return undefined;
  };

  private updateAutoSeekOnAdBreakEnd = (): void => {
    this.autoSeekPositions.shift();
    this.updatePolicyStatusOnInterruptionEnd();
  };

  private updatePolicyStatusOnInterruptionEnd = (): void => {
    if (!this.autoSeekPositions.length) {
      this.setIdle();
    } else {
      this.setAutoSeek();
    }

    this.logger.log(`[AD POLICY] ad policy in place: ${this.policyInPlace}`);
  };

  private setIdle = (): void => {
    if (this.autoSeekPositions.length) {
      this.policyInPlace = ActivePolicy.IDLE;
    }
  };

  private setAutoSeek = (): void => {
    if (this.autoSeekPositions.length) {
      this.policyInPlace = ActivePolicy.AUTO_SEEK;
    }
  };
}
